import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, MapPin, Loader2, Users } from "lucide-react";
import { api } from "@/lib/api";

const TERRA = "#c4704b";

const SERATE = [
  { id: "Serata I — Metà Ottobre 2026", label: "Serata I", when: "Metà Ottobre 2026", note: "data esatta da comunicare" },
  { id: "Serata II — Metà Novembre 2026", label: "Serata II", when: "Metà Novembre 2026", note: "data esatta da comunicare" },
  { id: "Serata III — Metà Novembre 2026", label: "Serata III", when: "Metà Novembre 2026", note: "data esatta da comunicare" },
  { id: "Tutte e 3 le serate (pacchetto €320)", label: "Tutte e 3 le serate", when: "Pacchetto completo", note: "€320 invece di €360" },
];

const PREFISSI = ["+39", "+41", "+43", "+44", "+33", "+49", "+34", "+32", "+1"];

export default function AssaggiSalento() {
  const [form, setForm] = useState({ nome: "", email: "", prefisso: "+39", telefono: "", serata: SERATE[0].id, fonte: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    api.get("/salento/waitlist/count").then((r) => setCount(r.data.count)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/salento/waitlist", {
        nome: form.nome,
        email: form.email,
        whatsapp: `${form.prefisso} ${form.telefono}`,
        serata: form.serata,
        fonte: form.fonte || null,
      });
      setDone(true);
      toast.success(data.message);
    } catch {
      toast.error("Errore nell'iscrizione, riprova.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="salento-page">
      {/* Hero */}
      <section className="relative border-b border-[#2A2A2A] overflow-hidden">
        <img src="/images/hero-assaggi.png" alt="Assaggi d'Arte Salento" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(10,10,10,0.92) 30%, rgba(196,112,75,0.28) 100%)" }} />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-10 py-24 lg:py-32">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] mb-5" style={{ color: TERRA }}>
            <MapPin size={14} /> Edizione speciale
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-5" data-testid="salento-hero-title">
            Assaggi d'Arte — <span style={{ color: TERRA }}>Salento</span>
          </h1>
          <p className="text-base sm:text-lg text-[#f5f5f5]">3 serate. 3 location segrete. 90 persone in tutto il Salento.</p>
          {count !== null && count > 0 && (
            <p className="flex items-center gap-2 mt-6 text-sm" style={{ color: TERRA }} data-testid="salento-counter">
              <Users size={16} />
              <span className="font-bold">{count} {count === 1 ? "persona è già" : "persone sono già"} in lista d'attesa</span>
              <span className="text-[#9a9484]">· 90 posti in tutto, non uno di più</span>
            </p>
          )}
        </div>
      </section>

      {/* Descrizione */}
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16" data-testid="salento-description">
          <p className="font-display text-2xl sm:text-3xl italic leading-snug mb-8">
            Non ti diciamo dove. Non ti diciamo il menu. Ti diciamo solo che non te lo dimentichi.
          </p>
          <div className="space-y-4 text-sm text-[#C9C4B8] leading-relaxed">
            <p>Tre cene d'arte in tre location segrete del Salento. Metà ottobre e metà novembre 2026.</p>
            <p>
              Chef Antonio Blandolino prepara 6 portate fine dining create per ogni serata. Italo Blandolino porta
              opere originali esposte a Cannes, Londra e Firenze. Asta live a fine serata.
            </p>
            <p>30 posti per serata. Vini abbinati. Un'esperienza irripetibile.</p>
          </div>
          <p className="font-display text-2xl font-black mt-8" style={{ color: TERRA }}>€120 a persona — tutto incluso.</p>
        </div>
      </section>

      {/* Form */}
      <section className="bg-[#141414]">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16">
          {done ? (
            <div className="text-center py-16" data-testid="salento-success">
              <CheckCircle2 size={56} className="mx-auto mb-6" style={{ color: TERRA }} />
              <h2 className="font-display text-4xl font-black mb-4">Sei dentro.</h2>
              <p className="text-sm text-[#C9C4B8] max-w-md mx-auto">
                Controlla la tua email: ti abbiamo appena scritto. Quando apriamo le vendite, tu avrai 24 ore di
                vantaggio sul resto del mondo.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="salento-form">
              <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: TERRA }}>Lista d'attesa</p>
              <h2 className="font-display text-2xl font-black mb-8">Scegli la tua serata</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10" data-testid="salento-serate">
                {SERATE.map((s) => (
                  <button type="button" key={s.id} onClick={() => setForm({ ...form, serata: s.id })}
                    data-testid={`salento-serata-${s.label.replaceAll(" ", "-")}`}
                    className="text-left border p-5 transition-colors"
                    style={form.serata === s.id
                      ? { borderColor: TERRA, background: "rgba(196,112,75,0.08)" }
                      : { borderColor: "#2A2A2A" }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-display text-lg font-bold">{s.label}</span>
                      <span className="w-4 h-4 rounded-full border flex items-center justify-center"
                        style={{ borderColor: form.serata === s.id ? TERRA : "#9a9484" }}>
                        {form.serata === s.id && <span className="w-2 h-2 rounded-full" style={{ background: TERRA }} />}
                      </span>
                    </div>
                    <p className="text-sm text-[#C9C4B8]">{s.when}</p>
                    <p className="text-[11px] text-[#9a9484] mt-1">{s.note}</p>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <input required minLength={2} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Nome e Cognome *" data-testid="salento-input-nome"
                  className="bg-[#0A0A0A] border border-[#2A2A2A] px-4 py-3 text-sm w-full focus:outline-none focus:border-[#c4704b]" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email *" data-testid="salento-input-email"
                  className="bg-[#0A0A0A] border border-[#2A2A2A] px-4 py-3 text-sm w-full focus:outline-none focus:border-[#c4704b]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="flex gap-2">
                  <select value={form.prefisso} onChange={(e) => setForm({ ...form, prefisso: e.target.value })}
                    data-testid="salento-input-prefisso"
                    className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-3 text-sm focus:outline-none focus:border-[#c4704b]">
                    {PREFISSI.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input required minLength={6} type="tel" value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value.replace(/[^0-9 ]/g, "") })}
                    placeholder="Numero WhatsApp *" data-testid="salento-input-whatsapp"
                    className="bg-[#0A0A0A] border border-[#2A2A2A] px-4 py-3 text-sm w-full focus:outline-none focus:border-[#c4704b]" />
                </div>
                <select value={form.fonte} onChange={(e) => setForm({ ...form, fonte: e.target.value })}
                  data-testid="salento-input-fonte"
                  className="bg-[#0A0A0A] border border-[#2A2A2A] px-4 py-3 text-sm w-full focus:outline-none focus:border-[#c4704b]"
                  style={{ color: form.fonte ? "#FCFCFC" : "#9a9484" }}>
                  <option value="">Come ci hai conosciuti? (opzionale)</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Passaparola">Passaparola</option>
                  <option value="Sito web">Sito web</option>
                  <option value="Altro">Altro</option>
                </select>
              </div>

              <button type="submit" disabled={sending} data-testid="salento-submit-button"
                className="w-full sm:w-auto text-black font-bold text-sm uppercase tracking-[0.2em] px-10 py-4 transition-opacity hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ background: TERRA }}>
                {sending && <Loader2 size={16} className="animate-spin" />} Entra nella lista
              </button>

              <p className="text-[11px] text-[#9a9484] leading-relaxed mt-6 max-w-xl" data-testid="salento-after-note">
                Riceverai un'email di conferma. 24 ore prima dell'apertura ufficiale delle vendite, sarai il primo a
                sapere dove, quando e come acquistare il tuo posto. La lista è gratuita e senza impegno.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
